import { useState } from 'react';
import { getCookie } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const useSponsorServices = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiCall = async (url, options = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = getCookie('token');
      
      // Build headers - don't set Content-Type if body is FormData (browser will set it with boundary)
      const headers = { ...options.headers };
      
      // Only set Content-Type if it's not FormData and not already set
      if (!(options.body instanceof FormData) && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
      }
      
      // Always add Authorization if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        headers,
        credentials: 'include',
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Something went wrong');
      }
      
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getPublicSponsors = async () => {
    return await apiCall('/api/v1/sponsors/public');
  };

  const getSponsorById = async (id) => {
    return await apiCall(`/api/v1/sponsors/${id}`);
  };

  // Admin functions
  const getAllSponsors = async (params = {}) => {
    const queryParams = new URLSearchParams();
    
    // Add pagination
    queryParams.append('page', params.page || 1);
    queryParams.append('limit', params.limit || 10);
    
    // Only add other params if they have values (not empty strings or undefined)
    Object.keys(params).forEach(key => {
      if (key !== 'page' && key !== 'limit' && params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    
    return await apiCall(`/api/v1/sponsors?${queryParams}`);
  };

  const createSponsor = async (sponsorData, logoFile = null) => {
    const formData = new FormData();
    Object.keys(sponsorData).forEach(key => {
      if (sponsorData[key] !== null && sponsorData[key] !== undefined) {
        formData.append(key, sponsorData[key]);
      }
    });
    if (logoFile) {
      formData.append('logoFile', logoFile);
    }
    
    return await apiCall('/api/v1/sponsors', {
      method: 'POST',
      body: formData
      // Don't set headers - apiCall will handle Authorization and let browser set Content-Type for FormData
    });
  };

  const updateSponsor = async (id, sponsorData, logoFile = null) => {
    const formData = new FormData();
    Object.keys(sponsorData).forEach(key => {
      if (sponsorData[key] !== null && sponsorData[key] !== undefined) {
        formData.append(key, sponsorData[key]);
      }
    });
    if (logoFile) {
      formData.append('logoFile', logoFile);
    }
    
    return await apiCall(`/api/v1/sponsors/${id}`, {
      method: 'PUT',
      body: formData
      // Don't set headers - apiCall will handle Authorization and let browser set Content-Type for FormData
    });
  };

  const toggleSponsorStatus = async (id) => {
    return await apiCall(`/api/v1/sponsors/${id}/toggle-status`, {
      method: 'PATCH'
    });
  };

  const deleteSponsor = async (id) => {
    return await apiCall(`/api/v1/sponsors/${id}`, {
      method: 'DELETE'
    });
  };

  return {
    getPublicSponsors,
    getSponsorById,
    getAllSponsors,
    createSponsor,
    updateSponsor,
    toggleSponsorStatus,
    deleteSponsor,
    loading,
    error
  };
};

